import { useEffect, useState } from "react";
import { euro, nlNum, http } from "../lib/api";

const REVEAL_KEY = "vl_saw_revealed";
const reduced = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export function SawDealCard({ maxPct }) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-[#34393E] text-white p-6" data-testid="saw-deal-card">
      <svg aria-hidden="true" className="absolute right-0 top-0 h-full w-24 opacity-70" viewBox="0 0 100 200" preserveAspectRatio="none">
        <path d="M70 0 L40 200" stroke="#B78B7B" strokeWidth="2" strokeDasharray="6 5" fill="none" />
      </svg>
      <p className="font-serif-brand text-2xl leading-snug pr-16">Mag het zaagverlies op onze rekening?</p>
      <p className="text-sm text-white/80 mt-2 pr-10 leading-relaxed">Voor deze vloer vergoeden we de eerste {maxPct}% zaagverlies, gerekend over jouw netto vloeroppervlak. Reken je met minder zaagverlies, dan vergoeden we dat lagere percentage. Bekijk jouw voordeel in de winkelwagen.</p>
    </div>
  );
}

function Plank({ run }) {
  return (
    <svg viewBox="0 0 320 56" className="w-full max-w-sm h-14" aria-hidden="true">
      <rect x="2" y="10" width="316" height="36" rx="6" fill="#EFE8E0" stroke="#D8CFC6" />
      {[40, 110, 190, 260].map((x) => <path key={x} d={`M${x} 18 q 18 6 36 0`} stroke="#D8CFC6" fill="none" />)}
      <line x1="250" y1="4" x2="250" y2="52" stroke="#B78B7B" strokeWidth="2.5" strokeLinecap="round" className={run ? "saw-cut" : ""} style={{ strokeDasharray: 48, strokeDashoffset: run ? 0 : 48 }} />
      <rect x="250" y="10" width="68" height="36" rx="6" fill="#B78B7B" className={run ? "saw-slide" : ""} opacity={run ? 0.85 : 0} />
    </svg>
  );
}

function NeedsArea({ l, onConfirm }) {
  const [area, setArea] = useState("");
  const [loss, setLoss] = useState(l.saw.pattern_pct);
  const [res, setRes] = useState(null);
  const m2 = parseFloat(String(area).replace(",", "."));
  useEffect(() => {
    if (!m2 || m2 <= 0) { setRes(null); return; }
    const t = setTimeout(() => http.post("/calc", { product_id: l.product_id, area_m2: m2, loss_pct: Number(loss) || 0 }).then((r) => setRes(r.data)).catch(() => setRes(null)), 250);
    return () => clearTimeout(t);
  }, [m2, loss, l.product_id]);
  const inp = "mt-1 w-full border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm bg-white text-[#272D30]";
  return (
    <div className="mt-3 bg-white rounded-xl p-4 space-y-3" data-testid={`saw-needs-area-${l.product_id}`}>
      <p className="text-sm font-medium">Vul je netto vloeroppervlak in</p>
      <p className="text-xs text-[#6E747B]">Het oppervlak van de ruimte zelf, zonder snijtoeslag. Zonder netto oppervlak kunnen we het zaagvoordeel niet berekenen.</p>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs text-[#6E747B]">Netto m²<input inputMode="decimal" className={inp} value={area} onChange={(e) => setArea(e.target.value)} placeholder="bijv. 60" data-testid={`saw-area-input-${l.product_id}`} /></label>
        <label className="text-xs text-[#6E747B]">Snijverlies %<input type="number" min="0" max="40" className={inp} value={loss} onChange={(e) => setLoss(e.target.value)} data-testid={`saw-loss-input-${l.product_id}`} /></label>
      </div>
      {res && <div className="text-xs bg-[#F2E6E0] rounded-lg px-3 py-2" data-testid={`saw-area-preview-${l.product_id}`}>Je hebt {res.packs} pakken nodig ({nlNum(res.delivered_m2, 3)} m²){res.packs !== l.packs ? ` – nu ${l.packs} in je winkelwagen` : ""}.</div>}
      {res && <button onClick={() => onConfirm({ area: res.area_m2, loss: res.loss_pct, packs: res.packs })} data-testid={`saw-area-confirm-${l.product_id}`}
        className="bg-[#34393E] hover:bg-[#B78B7B] text-white rounded-lg px-4 py-2 text-sm transition-colors">{res.packs !== l.packs ? `Bevestigen en aantal wijzigen naar ${res.packs} pakken` : "Bevestigen"}</button>}
    </div>
  );
}

export function SawResult({ l }) {
  const s = l.saw;
  if (!s?.eligible || s.needs_area) return null;
  if (s.superseded) return <p className="text-xs text-[#6E747B] mt-2">Voor deze vloer geldt een gunstiger voordeel (zie hieronder).</p>;
  return (
    <p className="text-xs text-[#34393E] mt-2 bg-[#F2E6E0] rounded-lg px-3 py-2" data-testid={`saw-result-${l.product_id}`}>
      Voor jouw {nlNum(s.net_m2, 3)} m² rekenen we {nlNum(s.loss_m2, 3)} m² zaagverlies. Daarvan nemen wij {nlNum(s.covered_m2, 3)} m² voor onze rekening
      {s.amount_cents > 0 ? <> · <strong>−{euro(s.amount_cents)}</strong></> : s.loss_pct === 0 ? " (bij 0% zaagverlies is er geen voordeel)." : " (je bestelt niet meer dan je netto oppervlak, dus er is geen zaagverlies te vergoeden)."}
    </p>
  );
}

export function useSawReveal() {
  const [revealed, setRevealed] = useState(() => localStorage.getItem(REVEAL_KEY) === "1");
  const reveal = () => { localStorage.setItem(REVEAL_KEY, "1"); setRevealed(true); };
  return [revealed, reveal];
}

export default function SawCartPanel({ q, revealed, reveal, update }) {
  const [run, setRun] = useState(false);
  const [busy, setBusy] = useState(false);
  const floors = q?.lines.filter((l) => l.kind === "floor" && l.saw?.eligible) || [];
  if (!floors.length) return null;
  const click = () => {
    if (busy || revealed) return;
    if (reduced()) { reveal(); return; }
    setBusy(true); setRun(true);
    setTimeout(() => { reveal(); setBusy(false); }, 1100);
  };
  return (
    <section className="bg-[#34393E] text-white rounded-2xl p-5 sm:p-6" data-testid="saw-cart-panel" aria-live="polite">
      <p className="font-serif-brand text-xl">Mag het zaagverlies op onze rekening?</p>
      {!revealed ? (
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-4">
          <Plank run={run} />
          <button onClick={click} disabled={busy} data-testid="saw-calc-button"
            className="shrink-0 bg-[#B78B7B] hover:bg-[#A27667] focus-visible:outline focus-visible:outline-2 focus-visible:outline-white text-white rounded-xl px-5 py-3 text-sm font-medium transition-colors">{busy ? "Zagen…" : "Bereken mijn zaagvoordeel"}</button>
        </div>
      ) : (
        <div className="mt-2 text-sm text-white/90 space-y-1" data-testid="saw-cart-summary">
          {q.saw_cents > 0 ? <p>Vlover Zaagvoordeel: <strong data-testid="saw-cart-total">−{euro(q.saw_cents)}</strong> op je vloersubtotaal. Het voordeel blijft actief als je je bestelling wijzigt.</p>
            : <p data-testid="saw-cart-total">Op dit moment is er geen zaagvoordeel{q.saw_needs_area ? ": vul hieronder je netto vloeroppervlak in." : "."}</p>}
        </div>
      )}
      {floors.filter((l) => l.saw.needs_area).map((l) => <div key={l.product_id} className="text-[#272D30]"><p className="text-xs text-white/70 mt-3">{l.name}</p><NeedsArea l={l} onConfirm={(patch) => update(l.product_id, patch)} /></div>)}
      {q.benefit_notes?.map((n) => <p key={n} className="text-xs text-white/80 mt-3" data-testid="saw-benefit-note">{n}</p>)}
    </section>
  );
}
